import { IfChkDocTagListEntry } from 'models/IfTagListEntity';
import React from 'react';
import { ICategoryMap } from 'repositories/category/CategoryRepository';
import {
  ISsqDataCreatePartnerDoc,
  ISsqDataDocumentAdminPopup,
  ITemporaryListData,
  IVersion,
} from 'repositories/SsqDataRepository';
import { ssqutil } from 'util/ssqutil';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  CMMNTY_WRITE_PAGE_TYPE,
  CmmntyDocWriteValidation,
  DOC_GRP,
  ICmmntyDocWriteValidation,
} from './docwrite-common';

export type ZstdDocWrite = {
  inputDoc: ISsqDataDocumentAdminPopup;
  setInputDoc: (
    inputDoc: ISsqDataDocumentAdminPopup | ((doc: ISsqDataDocumentAdminPopup) => ISsqDataDocumentAdminPopup)
  ) => void;
  taglist: Array<IfChkDocTagListEntry>;
  doctypes: Array<IfChkDocTagListEntry>;
  prods: Array<IfChkDocTagListEntry>;
  industs: Array<IfChkDocTagListEntry>;
  apploads: Array<IfChkDocTagListEntry>;
  setTaglist: (taglist: Array<IfChkDocTagListEntry>) => void;
  setDoctypes: (doctypes: Array<IfChkDocTagListEntry>) => void;
  setProds: (prods: Array<IfChkDocTagListEntry>) => void;
  setIndusts: (industs: Array<IfChkDocTagListEntry>) => void;
  setApploads: (apploads: Array<IfChkDocTagListEntry>) => void;
  inputPartner: ISsqDataCreatePartnerDoc;
  inputVer: IVersion;
  setInputPartner: (inputPartner: ISsqDataCreatePartnerDoc) => void;
  setInputVer: (inputVer: IVersion) => void;
  categoryMap: ICategoryMap;
  indstCtgryMap: ICategoryMap;
  appLoadCtgryMap: ICategoryMap;
  setCategoryMap: (categoryMap: ICategoryMap) => void;
  setIndstCtgryMap: (indstCtgryMap: ICategoryMap) => void;
  setAppLoadCtgryMap: (appLoadCtgryMap: ICategoryMap) => void;
  pageType: CMMNTY_WRITE_PAGE_TYPE;
  setPageType: (pageType: CMMNTY_WRITE_PAGE_TYPE) => void;
  validation: ICmmntyDocWriteValidation;
  setValidation: (validation: ICmmntyDocWriteValidation) => void;
  isUseVersionManagement: boolean;
  setIsUseVersionManagement: (isUseVersionManagement: boolean) => void;
  hasRegisteredNewVersion: boolean;
  setHasRegisteredNewVersion: (hasRegisteredNewVersion: boolean) => void;
  docgrp: DOC_GRP;
  setDocgrp: (docgrp: DOC_GRP) => void;
  descParsedResult: any;
  setDescParsedResult: (descParsedResult: any) => void;
  tempDataList: ITemporaryListData[];
  setTempDataList: (tempDataList: ITemporaryListData[]) => void;
  orgDocId: number | null;
  setOrgDocId: (orgDocId: number | null) => void;
  checkedTagCnt: number;
  setCheckedTagCnt: (checkedTagCnt: number) => void;
  onBeforeUnload: (e: any) => void;
  setOnBeforeUnload: (onBeforeUnload: (e: any) => void) => void;
  uploadingFiles: boolean;
  setUploadingFiles: (uploadingFiles: boolean) => void;

  showReqApprPopup: boolean;
  apprMessage: string;
  apprUserEmail: string;
  isLoadingRequest: boolean;
  running: boolean;
  modifierName: string;
  modifierNickName: string;
  modifierEmail: string;
  registTime?: string;
  managerId: string;
  partnerId: number;
  partnerName: string;
  mngrNickName: string;
  managerIds: string[];
  /** PARTNER_NOTI 대상 파트너. 0 = 전체 */
  selectedPtnrIds: number[];
  setSelectedPtnrIds: (selectedPtnrIds: number[]) => void;
  setShowReqApprPopup: (showReqApprPopup: boolean) => void;
  setApprMessage: (apprMessage: string) => void;
  setApprUserEmail: (apprUserEmail: string) => void;
  setIsLoadingRequest: (isLoadingRequest: boolean) => void;
  setRunning: (running: boolean) => void;
  setModifierName: (modifierName: string) => void;
  setModifierNickName: (modifierNickName: string) => void;
  setModifierEmail: (modifierEmail: string) => void;
  setRegistTime: (registTime: string) => void;
  setManagerId: (managerId: string) => void;
  setPartnerId: (partnerId: number) => void;
  setPartnerName: (partnerName: string) => void;
  setMngrNickName: (mngrNickName: string) => void;
  setManagerIds: (managerIds: string[]) => void;
  captcha: string;
  captchaErr: boolean;
  setCaptcha: (captcha: string) => void;
  setCaptchaErr: (captchaErr: boolean) => void;
};
export const useZstdDocWrite = create<ZstdDocWrite>()(
  devtools(
    (set) => ({
      inputDoc: undefined,
      setInputDoc: (
        inputDoc: ISsqDataDocumentAdminPopup | ((doc: ISsqDataDocumentAdminPopup) => ISsqDataDocumentAdminPopup)
      ) => {
        if (typeof inputDoc === 'function') {
          return set((state) => ({ inputDoc: inputDoc(state.inputDoc) }));
        }
        return set({ inputDoc });
      },
      taglist: [],
      doctypes: [],
      prods: [],
      industs: [],
      apploads: [],
      setTaglist: (taglist: Array<IfChkDocTagListEntry>) => set({ taglist }),
      setDoctypes: (doctypes: Array<IfChkDocTagListEntry>) => set({ doctypes }),
      setProds: (prods: Array<IfChkDocTagListEntry>) => set({ prods }),
      setIndusts: (industs: Array<IfChkDocTagListEntry>) => set({ industs }),
      setApploads: (apploads: Array<IfChkDocTagListEntry>) => set({ apploads }),
      inputPartner: undefined,
      inputVer: undefined,
      setInputPartner: (inputPartner: ISsqDataCreatePartnerDoc) => set({ inputPartner }),
      setInputVer: (inputVer: IVersion) => set({ inputVer }),
      categoryMap: undefined,
      indstCtgryMap: undefined,
      appLoadCtgryMap: undefined,
      setCategoryMap: (categoryMap: ICategoryMap) => set({ categoryMap }),
      setIndstCtgryMap: (indstCtgryMap: ICategoryMap) => set({ indstCtgryMap }),
      setAppLoadCtgryMap: (appLoadCtgryMap: ICategoryMap) => set({ appLoadCtgryMap }),
      pageType: undefined,
      setPageType: (pageType: CMMNTY_WRITE_PAGE_TYPE) => set({ pageType }),
      validation: new CmmntyDocWriteValidation(),
      setValidation: (validation: ICmmntyDocWriteValidation) => set({ validation }),
      isUseVersionManagement: false,
      setIsUseVersionManagement: (isUseVersionManagement: boolean) => set({ isUseVersionManagement }),
      hasRegisteredNewVersion: false,
      setHasRegisteredNewVersion: (hasRegisteredNewVersion: boolean) => set({ hasRegisteredNewVersion }),
      docgrp: undefined,
      setDocgrp: (docgrp: DOC_GRP) => set({ docgrp }),
      descParsedResult: undefined,
      setDescParsedResult: (descParsedResult: any) => set({ descParsedResult }),
      tempDataList: [],
      setTempDataList: (tempDataList: ITemporaryListData[]) => set({ tempDataList }),
      orgDocId: undefined,
      setOrgDocId: (orgDocId: number | null) => set({ orgDocId }),
      checkedTagCnt: 0,
      setCheckedTagCnt: (checkedTagCnt: number) => set({ checkedTagCnt }),
      onBeforeUnload: () => {},
      setOnBeforeUnload: (onBeforeUnload: (e: any) => void) => set({ onBeforeUnload }),
      uploadingFiles: false,
      setUploadingFiles: (uploadingFiles: boolean) => set({ uploadingFiles }),

      showReqApprPopup: false,
      apprMessage: '',
      apprUserEmail: '',
      isLoadingRequest: false,
      running: false,
      modifierName: '',
      modifierNickName: '',
      modifierEmail: '',
      registTime: undefined,
      managerId: '',
      partnerId: 0,
      partnerName: '',
      mngrNickName: '',
      managerIds: [],
      selectedPtnrIds: [0],
      setSelectedPtnrIds: (selectedPtnrIds: number[]) => set({ selectedPtnrIds }),
      setShowReqApprPopup: (showReqApprPopup: boolean) => set({ showReqApprPopup }),
      setApprMessage: (apprMessage: string) => set({ apprMessage }),
      setApprUserEmail: (apprUserEmail: string) => set({ apprUserEmail }),
      setIsLoadingRequest: (isLoadingRequest: boolean) => set({ isLoadingRequest }),
      setRunning: (running: boolean) => set({ running }),
      setModifierName: (modifierName: string) => set({ modifierName }),
      setModifierNickName: (modifierNickName: string) => set({ modifierNickName }),
      setModifierEmail: (modifierEmail: string) => set({ modifierEmail }),
      setRegistTime: (registTime: string) => set({ registTime }),
      setManagerId: (managerId: string) => set({ managerId }),
      setPartnerId: (partnerId: number) => set({ partnerId }),
      setPartnerName: (partnerName: string) => set({ partnerName }),
      setMngrNickName: (mngrNickName: string) => set({ mngrNickName }),
      setManagerIds: (managerIds: string[]) => set({ managerIds }),
      captcha: '',
      captchaErr: false,
      setCaptcha: (captcha: string) => set({ captcha }),
      setCaptchaErr: (captchaErr: boolean) => set({ captchaErr }),
    }),
    { name: 'ZstdDocWrite', enabled: !ssqutil.isProduction() }
  )
);

type WithStoreProps<T> = T & ZstdDocWrite;

// 4. withStore HOC 구현
export const withZstdDocWrite = <P extends object>(WrappedComponent: React.ComponentType<WithStoreProps<P>>) => {
  const WithStoreComponent = (props: P) => {
    const store = useZstdDocWrite();
    return <WrappedComponent {...props} {...store} />;
  };

  WithStoreComponent.displayName = `withZstdDocWrite(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithStoreComponent;
};
